import React, { useEffect } from "react";
import CustomAssignedItems from "../../CustomAvailableAssignedItems/CustomAssignedItems";
import { VIEW_TYPE } from "../../../enums/ViewType";
import DeleteModal from "../../../pages/Modals/DeleteModal";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Generic Assigned tab for a shift-resource section.
 *  - create: renders the local selection as a grid with client-side deletion.
 *  - update: renders the server assigned list with pagination; deletion drops
 *    items from the section selection (toRegister source) and from the visible
 *    page.
 * Domain-agnostic: the per-module config + handlers come from the `useAssignedApp`
 * hook passed in.
 *
 * @param {Object} props
 * @param {Function} props.useAssignedApp - Domain hook resolving config + delete handlers.
 * @param {string} props.resource - Resource type handled by this section.
 * @param {number|string|null|undefined} props.id - Shift id; absent in create.
 * @param {boolean} [props.readOnly] - View-only mode: hides the checkbox and
 *   disables deletion (no delete modal). Used by the list detail expand.
 * @param {JSX.Element} [props.renderModuleTitle] - Optional module title node;
 *   forwarded to CustomAssignedItems (used by the list detail expand).
 * @param {Function} [props.setDisplayModal] - Display setter of the modal holding
 *   the section, so it is hidden while the delete confirmation is open instead of
 *   stacking one modal over the other.
 * @param {boolean} [props.autoHeight=true] - Forwarded to CustomAssignedItems;
 *   set it to false when the section lives inside a modal, where the
 *   viewport-based height does not apply.
 */
const AssignedTabResource = (props) => {
    const {
        useAssignedApp,
        resource,
        id,
        readOnly = false,
        renderModuleTitle,
        setDisplayModal,
        autoHeight = true,
        selected,
        setSelected,
        selectForDelete,
        setSelectForDelete,
        isAllChecked,
        setIsAllChecked,
        triggerDelete,
        setTriggerDelete,
        searchElement,
        setSearchElement,
        results,
        setResults,
        setBodyForExport,
        setSearch,
        setSimpleSearch,
        modified,
        setModified,
    } = props;
    const {
        t,
        deletePermission,
        deleteModalShow,
        dynamicConfig,
        assignedEndPoint,
        buildBody,
        handleDelete,
        onDelete,
        hide,
    } = useAssignedApp({
        resource,
        id,
        selected,
        setSelected,
        selectForDelete,
        setSelectForDelete,
        results,
        setModified,
        triggerDelete,
        setTriggerDelete,
        setIsAllChecked,
        searchElement,
        readOnly,
    });

    const keyId = dynamicConfig.keyId;

    // Hide the modal holding the section while the delete confirmation is open
    useEffect(() => {
        if (!setDisplayModal) return;

        setDisplayModal(deleteModalShow ? 'd-none' : '');
    }, [deleteModalShow]);

    return (
        <div className={"px-3 pb-3"}>
            <CustomAssignedItems
                setBodyForExport={setBodyForExport}
                results={results}
                setResults={setResults}
                searchElement={searchElement}
                setSearchElement={setSearchElement}
                setSearch={setSearch}
                setSimpleSearch={setSimpleSearch}
                modified={modified}
                setModified={setModified}
                selected={selectForDelete}
                setSelected={setSelectForDelete}
                labelRowsPerPage={dynamicConfig.labelRowsPerPage}
                tableNotInformationLegend={dynamicConfig.tableNotInformationLegend}
                tableBuildBody={buildBody}
                tableEndPoint={assignedEndPoint}
                keyId={keyId}
                viewTypeName={VIEW_TYPE.GRID.name}
                isAllChecked={isAllChecked}
                setIsAllChecked={setIsAllChecked}
                onDelete={!readOnly ? handleDelete : undefined}
                checkbox={!readOnly}
                searchTitle={dynamicConfig.searchLabel}
                renderAssignedItem={dynamicConfig.renderAssignedItem}
                renderModuleTitle={renderModuleTitle}
                autoHeight={autoHeight}
            />

            {!readOnly && deleteModalShow &&
                <DeleteModal
                    show={deleteModalShow}
                    onHide={hide}
                    onClickFn={onDelete}
                    data={selectForDelete}
                    title_modal={t(dynamicConfig.title_modal)}
                    description={'are_you_sure_you_want_to_remove'}
                    element_modal={t(dynamicConfig.element_modal)}
                    buttonCancelText={'cancel'}
                />
            }
        </div>
    );
}

export default AssignedTabResource
